import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  fetchHourRates,
  fetchHourRate,
  createHourRate,
  updateHourRate,
  deleteHourRate,
} from "../api/offer-calculation-hour-rate";

export const getHourRatesAsync = createAsyncThunk(
  "offerCalculationHourRates/getHourRates",
  async (params, { rejectWithValue }) => {
    try {
      const response = await fetchHourRates(params);
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const getHourRateAsync = createAsyncThunk(
  "offerCalculationHourRates/getHourRate",
  async (id, { rejectWithValue }) => {
    try {
      const response = await fetchHourRate(id);
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const createHourRateAsync = createAsyncThunk(
  "offerCalculationHourRates/createHourRate",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await createHourRate(payload);
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const updateHourRateAsync = createAsyncThunk(
  "offerCalculationHourRates/updateHourRate",
  async ({ id, payload }, { rejectWithValue }) => {
    try {
      const response = await updateHourRate(id, payload);
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const deleteHourRateAsync = createAsyncThunk(
  "offerCalculationHourRates/deleteHourRate",
  async (id, { rejectWithValue }) => {
    try {
      const response = await deleteHourRate(id);
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

const initialState = {
  hourRates: [],
  hourRate: null,
  isLoading: false,
  error: null,
  errors: null,
  totalCount: 0,
  currentCount: 0,
};

const offerCalculationHourRateSlice = createSlice({
  name: "offerCalculationHourRates",
  initialState,
  reducers: {
    clearHourRate: (state) => {
      state.hourRate = null;
      state.error = null;
      state.errors = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Get Hour Rates
      .addCase(getHourRatesAsync.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getHourRatesAsync.fulfilled, (state, action) => {
        state.isLoading = false;
        state.hourRates = action.payload?.data || [];
        state.totalCount = action.payload?.totalCount || 0;
        state.currentCount = action.payload?.currentCount || 0;
      })
      .addCase(getHourRatesAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload?.response?.data?.errors || action.payload?.message || "Failed to fetch hour rates";
      })
      // Get Hour Rate
      .addCase(getHourRateAsync.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getHourRateAsync.fulfilled, (state, action) => {
        state.isLoading = false;
        state.hourRate = action.payload;
      })
      .addCase(getHourRateAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload?.response?.data?.errors || action.payload?.message || "Failed to fetch hour rate";
      })
      // Create Hour Rate
      .addCase(createHourRateAsync.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createHourRateAsync.fulfilled, (state, action) => {
        state.isLoading = false;
        state.hourRate = action.payload;
      })
      .addCase(createHourRateAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload?.response?.data?.errors || action.payload?.message || "Failed to create hour rate";
        state.errors = action.payload?.response?.data?.errors;
      })
      // Update Hour Rate
      .addCase(updateHourRateAsync.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateHourRateAsync.fulfilled, (state, action) => {
        state.isLoading = false;
        state.hourRate = action.payload;
      })
      .addCase(updateHourRateAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload?.response?.data?.errors || action.payload?.message || "Failed to update hour rate";
        state.errors = action.payload?.response?.data?.errors;
      })
      // Delete Hour Rate
      .addCase(deleteHourRateAsync.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteHourRateAsync.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(deleteHourRateAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload?.response?.data?.errors || action.payload?.message || "Failed to delete hour rate";
      });
  },
});

export const { clearHourRate } = offerCalculationHourRateSlice.actions;
export default offerCalculationHourRateSlice.reducer;

